import * as EmailValidator from 'email-validator';
import { InviteRow } from '@/types/adminInvites';
import { InviteColumns, InviteColumn } from '@/constants/inviteColumns';

// MM-42243: live, client-side per-row validation for the bulk-invite grid (req #4, gates req #6). It mirrors the
// column contract the server enforces so the operator can resolve problems in-grid before submitting: required cells
// must be filled, an email must look like an email, typed cells (whole-number, date, GUID) must parse, and no two rows
// may share an email or NPI (in-batch duplicates; a match against an EXISTING pending invite is not an error — the
// server updates that invite and reports the row as "Updated"). The five
// checkbox bools are always a valid true/false and need no check. Results are returned as the same
// "<rowIndex>|<columnKey>" -> message map the grid already consumes for highlighting, so parse-time and validation
// highlights share one rendering path. This is a usability gate, not the authority — the server re-validates on load.

const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const INTEGER_PATTERN = /^-?\d+$/;

export interface RowValidationResult {
  // "<rowIndex>|<columnKey>" -> human-readable reason.
  cellErrors: Record<string, string>;
  isValid: boolean;
}

function textValue(row: InviteRow, col: InviteColumn): string {
  const value = (row as Record<string, unknown>)[col.key as string];
  return value == null ? '' : String(value).trim();
}

// Returns the error message for one cell, or null when the cell is acceptable.
function validateCell(row: InviteRow, col: InviteColumn): string | null {
  // Checkbox bools always carry a valid value.
  if (col.type === 'bool')
    return null;

  const value = textValue(row, col);

  if (value === '')
    return col.required ? 'This field is required.' : null;

  if (col.key === 'email' && !EmailValidator.validate(value))
    return 'Enter a valid email address.';

  if (col.type === 'number' && !INTEGER_PATTERN.test(value))
    return 'Enter a whole number.';

  if (col.type === 'date' && isNaN(Date.parse(value)))
    return 'Enter a valid date.';

  if (col.key === 'createdByZbedgeId' && !GUID_PATTERN.test(value))
    return 'Enter a valid identifier (GUID).';

  return null;
}

export interface RowValidationOptions {
  // Rows to leave out entirely (e.g. rows already saved by a submit, which are read-only and never resent).
  skipRows?: Set<number>;
}

// The columns that identify a person: two rows sharing either one are duplicates.
const DUPLICATE_KEYS: { key: 'email' | 'npi'; label: string; normalize: (value: string) => string }[] = [
  { key: 'email', label: 'email', normalize: value => value.toLowerCase() },
  { key: 'npi', label: 'NPI', normalize: value => value }
];

// Flags every row whose email or NPI repeats another (non-skipped) row's, on that cell, naming the other row(s) by
// their 1-based grid number. Empty values never count as duplicates.
function findDuplicates(rows: InviteRow[], options: RowValidationOptions): Record<string, string> {
  const cellErrors: Record<string, string> = {};

  for (const dup of DUPLICATE_KEYS) {
    const groups = new Map<string, number[]>();
    rows.forEach((row, rowIndex) => {
      if (options.skipRows?.has(rowIndex))
        return;
      const raw = row[dup.key];
      const value = raw == null ? '' : String(raw).trim();
      if (value === '')
        return;
      const groupKey = dup.normalize(value);
      groups.set(groupKey, [...(groups.get(groupKey) ?? []), rowIndex]);
    });

    for (const indices of groups.values()) {
      if (indices.length < 2)
        continue;
      for (const rowIndex of indices) {
        const others = indices.filter(i => i !== rowIndex).map(i => i + 1).join(', ');
        cellErrors[`${rowIndex}|${dup.key}`] = `Duplicate ${dup.label} — also on row ${others}.`;
      }
    }
  }

  return cellErrors;
}

// Validates every cell of every row (bar skipped ones), then flags in-batch duplicates by email or NPI, producing the
// highlight map and an overall valid flag. A cell's own format error takes precedence over a duplicate message.
export function validateInviteRows(rows: InviteRow[], options: RowValidationOptions = {}): RowValidationResult {
  const cellErrors: Record<string, string> = {};

  rows.forEach((row, rowIndex) => {
    if (options.skipRows?.has(rowIndex))
      return;
    for (const col of InviteColumns) {
      // Hidden columns are not shown or edited in the grid, so they never raise a cell error the operator could fix.
      if (col.hidden)
        continue;
      const message = validateCell(row, col);
      if (message != null)
        cellErrors[`${rowIndex}|${col.key}`] = message;
    }
  });

  const merged = { ...findDuplicates(rows, options), ...cellErrors };
  return { cellErrors: merged, isValid: Object.keys(merged).length === 0 };
}
